import { useToast } from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import type { IHubPackage } from "@models";
import { useUpdateHubPackage } from "@query";
import { useCallback } from "react";

export function useRemoveFromCart() {
  const { t } = useTranslation();
  const toast = useToast();
  const { mutateAsync: updateHubPackage, isPending: isRemoving } =
    useUpdateHubPackage();

  const removeFromCart = useCallback(
    async (
      cartPackage: IHubPackage | undefined | null,
      catalogId: number,
      version: number | string,
    ) => {
      if (cartPackage == null) return false;

      const rels = cartPackage.catalogRelations ?? [];
      const idx = rels.findIndex(
        (r) =>
          r.catalogId === catalogId &&
          String(r.catalogVersion) === String(version),
      );

      if (idx < 0) return false;

      const nextRelations = rels.filter((_, i) => i !== idx);

      const body: IHubPackage = {
        ...cartPackage,
        catalogRelations: nextRelations,
      };

      try {
        await updateHubPackage(body);
        toast.add({
          title: t("hub.catalog_manager.package_remove_success", {
            defaultValue: "Removed from package",
          }),
          data: { type: "success" },
        });
        return true;
      } catch {
        toast.add({
          title: t("hub.checkout.remove_error", {
            defaultValue: "Could not remove item",
          }),
          data: { type: "error" },
        });
        return false;
      }
    },
    [t, toast, updateHubPackage],
  );

  return { removeFromCart, isRemoving };
}
